import { useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery } from "react-query";
import {
  useCreate,
  useNotify,
  SimpleForm,
  Create,
  TextInput,
  NumberInput,
  useResourceContext,
  required,
  number,
  useRefresh,
  useDataProvider,
  AutocompleteInput,
} from "react-admin";
import MuiDatePickerInput from "../../common/MuiDatePickerInput";
import SaveDeleteBtns from "../../saveDeleteBtns/SaveDeleteBtn";
import Transition from "../../transition/Transition";
import parserFormatNumInput from "../../../utils/parserFormatNumInput";

const validateRequiredNumber = [required(), number()];
const validateRequiredDate = [required()];

export default function SalesmanIncentiveRateRuleAddDialog({ open, setOpen }) {
  const [create] = useCreate();
  const notify = useNotify();
  const refresh = useRefresh();
  const resource = useResourceContext();
  const dataProvider = useDataProvider();

  const {
    data: companies,
    isLoading: isLoadingCompanies,
  } = useQuery(["companies/select", "getListNoParams"], () =>
    dataProvider.getListNoParams("companies/select")
  );

  const save = useCallback(
    async (values) => {
      try {
        await create(
          resource,
          { data: values },
          {
            returnPromise: true,
            onSuccess: () => {
              notify("ra.notification.created", {
                messageArgs: { smart_count: 1 },
              });
              setOpen(false);
              refresh();
            },
          }
        );
      } catch (error) {
        if (error.body && error.body.errors) {
          return error.body.errors;
        }
      }
    },
    [create, notify, refresh, resource, setOpen]
  );

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} keepMounted={false} TransitionComponent={Transition}>
      <Create title=" " sx={{ "& .RaCreate-main": { mt: 0 } }}>
        <SimpleForm
          warnWhenUnsavedChanges
          onSubmit={save}
          toolbar={<SaveDeleteBtns />}
          mode="onBlur"
          reValidateMode="onBlur"
        >
          <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", pt: 0 }}>
            <DialogTitle>Create Incentive Rate Rule</DialogTitle>
            <IconButton onClick={handleClose} sx={{ mr: "11px" }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <DialogContent>
            <AutocompleteInput
              variant="outlined"
              source="company_id"
              optionText="name"
              optionValue="id"
              isRequired
              choices={isLoadingCompanies ? [] : companies}
              fullWidth
              validate={[required()]}
            />
            <TextInput
              variant="outlined"
              source="min_sale_net"
              fullWidth
              isRequired
              validate={validateRequiredNumber}
              {...parserFormatNumInput}
            />
            <TextInput
              variant="outlined"
              source="max_sale_net"
              fullWidth
              validate={number()}
              {...parserFormatNumInput}
            />
            <NumberInput
              variant="outlined"
              source="rate_g"
              fullWidth
              step={0.0001}
              isRequired
              validate={validateRequiredNumber}
            />
            <NumberInput
              variant="outlined"
              source="rate_phm"
              fullWidth
              step={0.0001}
              isRequired
              validate={validateRequiredNumber}
            />
            <NumberInput
              variant="outlined"
              source="rate_z"
              fullWidth
              step={0.0001}
              isRequired
              validate={validateRequiredNumber}
            />
            <MuiDatePickerInput
              variant="outlined"
              source="valid_from"
              fullWidth
              isRequired
              validate={validateRequiredDate}
              maxDateSource="valid_to"
            />
            <MuiDatePickerInput
              variant="outlined"
              source="valid_to"
              fullWidth
              isRequired
              validate={validateRequiredDate}
              minDateSource="valid_from"
            />
          </DialogContent>
        </SimpleForm>
      </Create>
    </Dialog>
  );
}
